import { useState, useRef, useMemo, useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useCreateHotTake, useHotTakeImageUpload, useHotTakeVideoUpload, useTeamsForSport } from '../../hooks/useHotTakes'
import { useActiveSports } from '../../hooks/useGames'
import { useSearchUsers } from '../../hooks/useInvitations'
import { useProfile } from '../../hooks/useProfile'
import Avatar from '../ui/Avatar'
import InfoTooltip from '../ui/InfoTooltip'
import TeamAutocomplete from './TeamAutocomplete'
import { toast } from '../ui/Toast'
import { supabase } from '../../lib/supabase'
import { parseEmbedSource } from '../../lib/embedParser'
import { extractFirstUrl } from '../../lib/urlUtils'
import LinkPreview from './LinkPreview'
import PostEmbed from './PostEmbed'

const MAX_CHARS = 2000
const IMAGE_URL_REGEX = /(?:https?:\/\/|www\.)[^\s]+\.(jpg|jpeg|png|gif|webp)(\?[^\s]*)?/gi

const sportTabs = [
  { label: 'NBA', key: 'basketball_nba' },
  { label: 'NCAAB', key: 'basketball_ncaab' },
  { label: 'WNCAAB', key: 'basketball_wncaab' },
  { label: 'MLB', key: 'baseball_mlb' },
  { label: 'NFL', key: 'americanfootball_nfl' },
  { label: 'NHL', key: 'icehockey_nhl' },
  { label: 'MLS', key: 'soccer_usa_mls' },
  { label: 'WNBA', key: 'basketball_wnba' },
  { label: 'NCAAF', key: 'americanfootball_ncaaf' },
]

const POST_TYPES = [
  { key: 'post', color: 'bg-purple-500', label: 'Post', placeholder: 'Talk to us.' },
  { key: 'prediction', color: 'bg-green-500', label: 'Prediction', placeholder: "What's your prediction?" },
  { key: 'poll', color: 'bg-orange-500', label: 'Poll', placeholder: 'Create a poll' },
]

export default function HotTakeComposer({ initialTeamTags = [] }) {
  const [content, setContent] = useState('')
  const [postType, setPostType] = useState('post')
  const [pollOptions, setPollOptions] = useState(['', ''])
  const [teamTags, setTeamTags] = useState(initialTeamTags)
  const [selectedSport, setSelectedSport] = useState(null)
  const [teamSearch, setTeamSearch] = useState('')
  const [expanded, setExpanded] = useState(false)
  const [userTags, setUserTags] = useState([])
  const [mentionQuery, setMentionQuery] = useState('')
  const createHotTake = useCreateHotTake()
  const queryClient = useQueryClient()
  const { data: profile } = useProfile()
  const { uploading, previewUrls, selectImage, removeImage, uploadImage, hasImage, imageCount } = useHotTakeImageUpload()
  const { uploading: videoUploading, progress: videoProgress, previewUrl: videoPreviewUrl, selectVideo, removeVideo, uploadVideo, hasVideo } = useHotTakeVideoUpload()
  const fileInputRef = useRef(null)
  const textareaRef = useRef(null)

  const { data: activeSports } = useActiveSports()
  const { data: teams } = useTeamsForSport(selectedSport)
  const { data: mentionResults } = useSearchUsers(mentionQuery)

  // Sort sport tabs: active sports first
  const sortedSportTabs = useMemo(() => {
    if (!activeSports?.length) return sportTabs
    const activeKeys = new Set(activeSports.map((s) => s.key))
    return [...sportTabs].sort((a, b) => {
      const aActive = activeKeys.has(a.key) ? 0 : 1
      const bActive = activeKeys.has(b.key) ? 0 : 1
      return aActive - bActive
    })
  }, [activeSports])

  // Inline content autocomplete — extract current word at cursor
  const [inlineMatches, setInlineMatches] = useState([])
  const [inlineDropdownPos, setInlineDropdownPos] = useState(null)

  // Embed state — raw user input for YouTube / X paste. Client parses for
  // an inline preview; server re-parses on submit for the authoritative
  // structured store. Cleared on post.
  const [embedInput, setEmbedInput] = useState('')
  const [showEmbedInput, setShowEmbedInput] = useState(false)
  const parsedEmbed = useMemo(() => parseEmbedSource(embedInput), [embedInput])

  // Live preview of a link pasted into the post body, so the author can see
  // the video render BEFORE publishing instead of guessing.
  //
  // Deliberately the same <LinkPreview> the feed card uses, driven by the
  // same extractFirstUrl — a bespoke preview could disagree with what
  // actually publishes, which is the very doubt this is meant to remove.
  // Suppressed when a structured embed exists, matching the feed card's rule
  // so the same video never shows twice.
  const contentUrl = useMemo(() => extractFirstUrl(content), [content])
  const [debouncedUrl, setDebouncedUrl] = useState(null)
  useEffect(() => {
    // Each keystroke makes a distinct URL string, and useLinkPreview keys its
    // cache on the URL — without this, typing a link fires a request per
    // character. Settle first, then fetch once.
    const t = setTimeout(() => setDebouncedUrl(contentUrl), 500)
    return () => clearTimeout(t)
  }, [contentUrl])

  // Extract image URLs from content, store separately, strip from visible text
  const [imageUrlsFromText, setImageUrlsFromText] = useState([])

  function handleContentChange(e) {
    let val = e.target.value
    const cursorPos = e.target.selectionStart

    // Detect image URLs, extract them, remove from text
    const imgMatches = val.match(IMAGE_URL_REGEX)
    if (imgMatches?.length) {
      const newUrls = imgMatches.map((u) => u.startsWith('http') ? u : `https://${u}`)
      setImageUrlsFromText((prev) => {
        const existing = new Set(prev)
        const merged = [...prev]
        for (const u of newUrls) {
          if (!existing.has(u)) merged.push(u)
        }
        return merged
      })
      // Remove the URL from the text content
      for (const match of imgMatches) {
        val = val.replace(match, '').trim()
      }
    }

    setContent(val)

    // Detect @mention at cursor position
    const beforeCursor = val.slice(0, cursorPos)
    const mentionMatch = beforeCursor.match(/(^|\s)@(\w*)$/)
    if (mentionMatch) {
      setMentionQuery(mentionMatch[2].length >= 2 ? mentionMatch[2] : '')
      setInlineMatches([])
      setInlineDropdownPos(null)
      return
    }
    setMentionQuery('')

    if (!teams?.length || !selectedSport) {
      setInlineMatches([])
      setInlineDropdownPos(null)
      return
    }

    // Get the last word being typed (split on whitespace)
    const words = val.split(/\s+/)
    const currentWord = (words[words.length - 1] || '').toLowerCase()

    if (currentWord.length >= 3) {
      const matches = teams.filter((t) => {
        const lower = t.toLowerCase()
        if (lower.includes(currentWord)) return true
        return lower.split(/\s+/).some((w) => w.startsWith(currentWord))
      }).filter((t) => !teamTags.includes(t)).slice(0, 6)
      setInlineMatches(matches)
      setInlineDropdownPos(matches.length > 0 ? { show: true } : null)
    } else {
      setInlineMatches([])
      setInlineDropdownPos(null)
    }
  }

  function handleInlineSelect(teamName) {
    if (teamTags.length < 5 && !teamTags.includes(teamName)) {
      setTeamTags([...teamTags, teamName])
    }
    setInlineMatches([])
    setInlineDropdownPos(null)
    textareaRef.current?.focus()
  }

  function handleMentionSelect(user) {
    if (userTags.length >= 3 || userTags.some((u) => u.id === user.id)) return
    const textarea = textareaRef.current
    const cursorPos = textarea?.selectionStart || content.length
    const beforeCursor = content.slice(0, cursorPos)
    const afterCursor = content.slice(cursorPos)
    const match = beforeCursor.match(/@(\w*)$/)
    if (match) {
      const prefix = beforeCursor.slice(0, beforeCursor.length - match[0].length)
      const inserted = `@${user.username} `
      const newContent = prefix + inserted + afterCursor
      const newPos = prefix.length + inserted.length
      setContent(newContent)
      setUserTags([...userTags, user])
      setMentionQuery('')
      setTimeout(() => {
        if (textarea) {
          textarea.selectionStart = newPos
          textarea.selectionEnd = newPos
          textarea.focus()
        }
      }, 0)
    }
  }

  function addTeamTag(teamName) {
    if (teamTags.length < 5 && !teamTags.includes(teamName)) {
      setTeamTags([...teamTags, teamName])
    }
  }

  function removeTeamTag(teamName) {
    setTeamTags(teamTags.filter((t) => t !== teamName))
  }

  const charCount = content.length
  const pollValid = postType !== 'poll' || pollOptions.filter((o) => o.trim()).length >= 2
  const hasContent = charCount > 0 || hasImage || hasVideo || !!parsedEmbed
  const canPost = hasContent && charCount <= MAX_CHARS && !createHotTake.isPending && !uploading && !videoUploading && pollValid

  async function handlePost() {
    if (!canPost) return

    let imageUrl = undefined
    let imageUrls = undefined
    if (hasImage) {
      const urls = await uploadImage()
      if (!urls && hasImage) return // upload failed
      imageUrls = urls
      imageUrl = urls?.[0]
    }

    let videoUrl = undefined
    let streamVideoUid = undefined
    if (hasVideo) {
      const result = await uploadVideo()
      if (!result && hasVideo) return // upload failed
      // Cloudflare Stream direct-upload returns { hlsUrl, uid } — old
      // Supabase-storage path returned a public URL string. Handle both.
      if (typeof result === 'string') {
        videoUrl = result
      } else if (result && typeof result === 'object') {
        videoUrl = result.hlsUrl
        streamVideoUid = result.uid
      }
    }

    const trimmedPollOptions = postType === 'poll' ? pollOptions.filter((o) => o.trim()).map((o) => o.trim()) : undefined

    // Re-append image URLs to content so they render inline via RichContent
    const finalContent = imageUrlsFromText.length
      ? [content.trim(), ...imageUrlsFromText].filter(Boolean).join('\n')
      : content.trim()

    createHotTake.mutate(
      { content: finalContent, team_tags: teamTags.length ? teamTags : undefined, sport_key: selectedSport || undefined, image_url: imageUrl, image_urls: imageUrls, video_url: videoUrl, stream_video_uid: streamVideoUid, user_tags: userTags.length ? userTags.map((u) => u.id) : undefined, post_type: postType, poll_options: trimmedPollOptions, embed_source: parsedEmbed ? embedInput.trim() : undefined },
      {
        onSuccess: () => {
          setContent('')
          setPostType('post')
          setPollOptions(['', ''])
          setTeamTags(initialTeamTags)
          setUserTags([])
          setMentionQuery('')
          setSelectedSport(null)
          setTeamSearch('')
          setExpanded(false)
          setImageUrlsFromText([])
          setEmbedInput('')
          setShowEmbedInput(false)
          removeImage()
          removeVideo()
          queryClient.invalidateQueries({ queryKey: ['hotTakes', 'team'] })
          queryClient.invalidateQueries({ queryKey: ['hotTakes', 'sport'] })
        },
        onError: (err) => {
          if (err.status === 403) {
            toast('Your posting privileges have been suspended. Contact support if you believe this is an error.', 'error')
          } else {
            toast(err.message || 'Failed to post', 'error')
          }
        },
      }
    )
  }

  function handleCancel() {
    setExpanded(false)
    setContent('')
    setPostType('post')
    setPollOptions(['', ''])
    setTeamTags(initialTeamTags)
    setUserTags([])
    setMentionQuery('')
    setSelectedSport(null)
    setTeamSearch('')
    setImageUrlsFromText([])
    setEmbedInput('')
    setShowEmbedInput(false)
    removeImage()
    removeVideo()
  }

  function handleFileChange(e) {
    const files = e.target.files
    if (!files?.length) { e.target.value = ''; return }
    for (const file of files) {
      if (file.type.startsWith('video/')) {
        removeImage()
        selectVideo(file)
        break
      } else {
        removeVideo()
        selectImage(file)
      }
    }
    e.target.value = ''
  }

  function handlePaste(e) {
    const items = e.clipboardData?.items
    if (!items) return
    for (const item of items) {
      if (item.type.startsWith('image/')) {
        e.preventDefault()
        const file = item.getAsFile()
        if (file) selectImage(file)
        return
      }
    }
  }

  const [dragging, setDragging] = useState(false)

  function handleDragOver(e) {
    e.preventDefault()
    setDragging(true)
  }

  function handleDragLeave(e) {
    e.preventDefault()
    setDragging(false)
  }

  function extractImgSrcFromHtml(html) {
    if (!html) return null
    const m = html.match(/<img[^>]+src=["']([^"']+)["']/i)
    return m?.[1] || null
  }

  function blobToFile(blob) {
    const ext = (blob.type.split('/')[1] || 'png').replace('jpeg', 'jpg')
    return new File([blob], `dropped.${ext}`, { type: blob.type })
  }

  async function fetchUrlAsFile(url) {
    // Path A: direct fetch — works for same-origin or CORS-permissive hosts.
    try {
      const res = await fetch(url, { mode: 'cors' })
      if (res.ok) {
        const blob = await res.blob()
        if (blob.type.startsWith('image/')) return blobToFile(blob)
      }
    } catch (err) {
      // CORS failure throws TypeError; fall through to proxy.
      console.warn('[drop] direct fetch failed, will try server proxy:', err.message)
    }

    // Path B: server proxy. Browser CORS doesn't apply server-to-server.
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const headers = session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}
      const proxyRes = await fetch(`/api/image-proxy?url=${encodeURIComponent(url)}`, { headers })
      if (!proxyRes.ok) {
        const errBody = await proxyRes.json().catch(() => ({}))
        console.warn('[drop] proxy returned', proxyRes.status, errBody)
        return null
      }
      const blob = await proxyRes.blob()
      return blobToFile(blob)
    } catch (err) {
      console.warn('[drop] proxy fetch threw:', err.message)
      return null
    }
  }

  async function handleDrop(e) {
    e.preventDefault()
    setDragging(false)

    // Read dataTransfer state synchronously — it may be cleared on yield.
    const dt = e.dataTransfer
    const directFile = dt?.files?.[0] || null
    let itemFile = null
    if (dt?.items) {
      for (const item of dt.items) {
        if (item.kind === 'file') {
          const f = item.getAsFile()
          if (f) { itemFile = f; break }
        }
      }
    }
    const url =
      (dt?.getData('text/uri-list') || '').trim() ||
      extractImgSrcFromHtml(dt?.getData('text/html')) ||
      (dt?.getData('text/plain') || '').trim()

    console.log('[drop] sources:', {
      directFile: directFile ? `${directFile.name} (${directFile.type})` : null,
      itemFile: itemFile ? `${itemFile.name} (${itemFile.type})` : null,
      url: url || null,
      types: dt ? Array.from(dt.types) : [],
    })

    let file = directFile || itemFile
    if (!file && url) {
      file = await fetchUrlAsFile(url)
      if (!file) {
        toast('Could not load that image. Try saving it and uploading directly.', 'error')
        return
      }
    }

    if (!file) {
      console.warn('[drop] no usable file or URL found in dataTransfer')
      return
    }

    if (file.type.startsWith('video/')) {
      removeImage()
      selectVideo(file)
    } else if (file.type.startsWith('image/')) {
      removeVideo()
      selectImage(file)
    } else {
      console.warn('[drop] unsupported file type:', file.type)
    }
    if (!expanded) setExpanded(true)
  }

  return (
    <div
      className={`bg-bg-primary/60 backdrop-blur-2xl border rounded-xl px-4 py-3 mb-4 transition-all ${
        dragging ? 'border-accent border-dashed bg-accent/5' : expanded ? 'border-accent/20' : 'border-text-primary/20'
      }`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <div className="flex gap-3">
        {/* Post type selector */}
        <div className="flex flex-col gap-1.5 pt-1">
          {POST_TYPES.map((type) => (
            <button
              key={type.key}
              onClick={() => { setPostType(type.key); if (!expanded) setExpanded(true) }}
              title={type.label}
              className={`w-7 h-7 rounded-full transition-all ${type.color} ${
                postType === type.key ? 'ring-2 ring-white scale-110' : 'opacity-40 hover:opacity-70'
              }`}
            />
          ))}
        </div>

        <div className="flex-1 min-w-0">
          {/* Textarea with inline autocomplete */}
          <div className="relative">
            <textarea
              ref={textareaRef}
              value={content}
              onChange={handleContentChange}
              onPaste={handlePaste}
              onFocus={() => setExpanded(true)}
              placeholder={POST_TYPES.find((t) => t.key === postType)?.placeholder || 'Talk to us.'}
              maxLength={MAX_CHARS}
              rows={expanded ? 3 : 1}
              className="w-full bg-transparent text-base text-text-primary placeholder-text-muted resize-none outline-none transition-all"
            />

            {/* Draft preview — what the post will look like once published */}
            {expanded && !parsedEmbed && debouncedUrl && (
              <div className="mt-2" onClick={(e) => e.stopPropagation()}>
                <LinkPreview url={debouncedUrl} />
              </div>
            )}

            {/* Inline team autocomplete dropdown */}
            {inlineDropdownPos?.show && inlineMatches.length > 0 && (
              <div className="absolute z-50 left-0 right-0 top-full mt-1 bg-bg-primary border border-text-primary/20 rounded-lg shadow-lg max-h-40 overflow-y-auto">
                {inlineMatches.map((t) => (
                  <button
                    key={t}
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => handleInlineSelect(t)}
                    className="w-full text-left px-3 py-1.5 text-xs text-text-primary hover:bg-text-primary/5 truncate"
                  >
                    {t}
                  </button>
                ))}
              </div>
            )}

            {/* @mention autocomplete dropdown */}
            {mentionQuery.length >= 2 && mentionResults?.length > 0 && (
              <div className="absolute z-50 left-0 right-0 top-full mt-1 bg-bg-primary border border-text-primary/20 rounded-lg shadow-lg max-h-40 overflow-y-auto">
                {mentionResults
                  .filter((u) => !userTags.some((t) => t.id === u.id))
                  .slice(0, 5)
                  .map((u) => (
                    <button
                      key={u.id}
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => handleMentionSelect(u)}
                      className="w-full flex items-center gap-2 px-3 py-1.5 text-left hover:bg-text-primary/5 transition-colors"
                    >
                      <Avatar user={u} size="xs" />
                      <div className="min-w-0">
                        <div className="text-xs text-text-primary truncate">{u.display_name || u.username}</div>
                        <div className="text-[10px] text-text-muted truncate">@{u.username}</div>
                      </div>
                    </button>
                  ))}
              </div>
            )}
          </div>

          {/* Image URL preview (from pasted URLs) */}
          {imageUrlsFromText.length > 0 && previewUrls.length === 0 && (
            <div className="mt-2 space-y-2">
              {imageUrlsFromText.map((url, i) => (
                <div key={i} className="relative inline-block">
                  <img
                    src={url}
                    alt="Preview"
                    className="max-w-full max-h-48 rounded-lg object-contain"
                    onError={(e) => { e.target.style.display = 'none' }}
                  />
                  <button
                    onClick={() => setImageUrlsFromText((prev) => prev.filter((_, j) => j !== i))}
                    className="absolute top-1 right-1 w-5 h-5 bg-black/70 rounded-full flex items-center justify-center text-white text-xs hover:bg-black/90"
                  >
                    &times;
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Image previews */}
          {previewUrls.length > 0 && (
            <div className="flex gap-2 mt-2 overflow-x-auto">
              {previewUrls.map((url, i) => (
                <div key={url} className="relative shrink-0">
                  <img
                    src={url}
                    alt={`Upload preview ${i + 1}`}
                    className="h-24 w-24 rounded-lg object-cover"
                  />
                  <button
                    onClick={() => removeImage(i)}
                    className="absolute top-1 right-1 w-5 h-5 bg-black/70 rounded-full flex items-center justify-center text-white text-xs hover:bg-black/90 transition-colors"
                  >
                    ×
                  </button>
                </div>
              ))}
              {imageCount < 4 && (
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="h-24 w-24 rounded-lg border border-dashed border-border flex items-center justify-center text-text-muted hover:border-accent/50 hover:text-accent transition-colors shrink-0"
                >
                  <span className="text-xl">+</span>
                </button>
              )}
            </div>
          )}

          {/* Video preview */}
          {videoPreviewUrl && (
            <div className="relative mt-2 inline-block">
              <video
                src={videoPreviewUrl}
                controls
                className="max-h-48 rounded-lg"
              />
              <button
                onClick={removeVideo}
                className="absolute top-1 right-1 w-5 h-5 bg-black/70 rounded-full flex items-center justify-center text-white text-xs hover:bg-black/90 transition-colors"
              >
                ×
              </button>
            </div>
          )}

          {/* Embed input + preview */}
          {(showEmbedInput || parsedEmbed) && (
            <div className="mt-2 space-y-2">
              {showEmbedInput && (
                <textarea
                  value={embedInput}
                  onChange={(e) => setEmbedInput(e.target.value)}
                  placeholder="Paste YouTube / X link or embed code…"
                  rows={3}
                  className="w-full bg-bg-surface border border-text-primary/20 rounded-lg px-3 py-2 text-sm text-text-primary placeholder-text-muted focus:outline-none focus:ring-1 focus:ring-accent resize-y font-mono"
                />
              )}
              {parsedEmbed && (
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-accent/10 border border-accent/40 text-xs">
                  <span className="font-semibold text-accent">
                    {parsedEmbed.provider === 'youtube' ? 'YouTube video' : 'X post'}
                  </span>
                  <span className="text-text-muted truncate max-w-[160px]">#{parsedEmbed.refId}</span>
                  <button
                    onClick={() => { setEmbedInput(''); setShowEmbedInput(false) }}
                    className="text-text-muted hover:text-text-primary leading-none ml-1"
                    aria-label="Remove embed"
                  >
                    ×
                  </button>
                </div>
              )}
              {/* Render the actual embed, not just a label. Same PostEmbed the
                  feed card uses, from the same (provider, refId) tuple the
                  server will store — so the draft is the published result. */}
              {parsedEmbed && (
                <div onClick={(e) => e.stopPropagation()}>
                  <PostEmbed provider={parsedEmbed.provider} refId={parsedEmbed.refId} />
                </div>
              )}
              {showEmbedInput && embedInput.trim() && !parsedEmbed && (
                <div className="text-xs text-yellow-500">
                  Doesn't look like a YouTube / X link or embed code — post will send without an embed.
                </div>
              )}
            </div>
          )}

          {/* Poll options */}
          {postType === 'poll' && expanded && (
            <div className="mt-2 space-y-1.5">
              {pollOptions.map((opt, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input
                    value={opt}
                    onChange={(e) => {
                      const next = [...pollOptions]
                      next[i] = e.target.value
                      setPollOptions(next)
                    }}
                    placeholder={`Option ${i + 1}`}
                    maxLength={100}
                    className="flex-1 bg-bg-surface border border-text-primary/20 rounded-lg px-3 py-1.5 text-sm text-text-primary placeholder-text-muted focus:outline-none focus:ring-1 focus:ring-orange-500"
                  />
                  {pollOptions.length > 2 && (
                    <button
                      onClick={() => setPollOptions(pollOptions.filter((_, j) => j !== i))}
                      className="text-text-muted hover:text-incorrect text-xs"
                    >
                      ×
                    </button>
                  )}
                </div>
              ))}
              {pollOptions.length < 10 && (
                <button
                  onClick={() => setPollOptions([...pollOptions, ''])}
                  className="text-xs text-accent hover:text-accent-hover transition-colors"
                >
                  + Add option
                </button>
              )}
            </div>
          )}

          {expanded && (
            <>
              {/* Sport chips row */}
              <div className="mt-2 flex gap-1.5 overflow-x-auto no-scrollbar pb-1">
                {sortedSportTabs.map((sport) => (
                  <button
                    key={sport.key}
                    onClick={() => {
                      if (selectedSport === sport.key) {
                        setSelectedSport(null)
                        setTeamSearch('')
                      } else {
                        setSelectedSport(sport.key)
                        setTeamSearch('')
                      }
                    }}
                    className={`px-2.5 py-1 rounded-full text-[10px] font-semibold whitespace-nowrap transition-colors ${
                      selectedSport === sport.key
                        ? 'bg-accent text-white'
                        : 'bg-bg-primary border border-text-primary/20 text-text-primary hover:border-text-primary/40'
                    }`}
                  >
                    {sport.label}
                  </button>
                ))}
              </div>

              {/* Team tag pills */}
              {teamTags.length > 0 && (
                <div className="mt-1.5 flex flex-wrap gap-1">
                  {teamTags.map((tag) => (
                    <span
                      key={tag}
                      className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-accent px-2 py-0.5 rounded-full"
                    >
                      {tag}
                      <button
                        onClick={() => removeTeamTag(tag)}
                        className="hover:text-white transition-colors leading-none"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}

              {/* User tag pills */}
              {userTags.length > 0 && (
                <div className="mt-1.5 flex flex-wrap gap-1">
                  {userTags.map((u) => (
                    <span
                      key={u.id}
                      className="inline-flex items-center gap-1 text-[10px] font-semibold bg-purple-500/15 text-purple-400 px-2 py-0.5 rounded-full"
                    >
                      @{u.username}
                      <button
                        onClick={() => setUserTags(userTags.filter((t) => t.id !== u.id))}
                        className="hover:text-white transition-colors leading-none"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}

              {/* Tagging row — full width under the post box so the hint
                  reads as one clean horizontal line instead of getting
                  vertically wedged into the action row alongside icons. */}
              {(selectedSport ? teamTags.length < 5 : true) && (
                <div className="mt-2">
                  {selectedSport ? (
                    <TeamAutocomplete
                      teams={(teams || []).filter((t) => !teamTags.includes(t))}
                      onSelect={addTeamTag}
                      inputValue={teamSearch}
                      onInputChange={setTeamSearch}
                      placeholder="Tag team..."
                    />
                  ) : (
                    <span className="text-[11px] text-text-muted">Pick a sport to tag teams</span>
                  )}
                </div>
              )}

              <div className="mt-2 flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  {/* Image upload button */}
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="text-text-muted hover:text-text-secondary transition-colors p-1"
                    title="Photo/video"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                      <circle cx="12" cy="13" r="4" />
                    </svg>
                  </button>
                  <button
                    onClick={() => setShowEmbedInput((v) => !v)}
                    className={`transition-colors p-1 ${showEmbedInput || parsedEmbed ? 'text-accent' : 'text-text-muted hover:text-text-secondary'}`}
                    title="Embed YouTube or X"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="16 18 22 12 16 6" />
                      <polyline points="8 6 2 12 8 18" />
                    </svg>
                  </button>
                  <InfoTooltip text="Share posts, predictions, and polls. Tag teams so fans can find them. Your squad can react, comment, and remind you of your predictions later. Bookmark predictions to save receipts." />
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*,video/*"
                    multiple
                    onChange={handleFileChange}
                    className="hidden"
                  />

                  {charCount > 0 && (
                    <span className={`text-xs flex-shrink-0 ${charCount > MAX_CHARS ? 'text-incorrect' : 'text-text-muted'}`}>
                      {charCount}/{MAX_CHARS}
                    </span>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={handleCancel}
                    className="text-xs text-text-muted hover:text-text-secondary transition-colors"
                  >
                    Cancel
                  </button>
                  {hasContent && (
                    <button
                      onClick={handlePost}
                      disabled={!canPost}
                      className="bg-accent text-white text-xs font-semibold px-4 py-1.5 rounded-lg disabled:opacity-50 transition-opacity"
                    >
                      {videoUploading && videoProgress > 0
                        ? `Uploading ${videoProgress}%`
                        : createHotTake.isPending || uploading || videoUploading ? 'Posting...' : 'Post'}
                    </button>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
